import { FormEvent, useEffect, useState } from "react";
import { useData } from "@/contexts/DataContext";
import { Eye, EyeOff, LinkIcon, Mail, Newspaper, Briefcase, Lock, TrendingUp } from "lucide-react";
import { authService } from "@/lib/authService";

const AdminDashboard = () => {
  const { ipos, news, jobs, affiliates } = useData();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEnvBackupAdmin, setIsEnvBackupAdmin] = useState(false);

  const stats = [
    { label: "Total IPOs", value: ipos.length, icon: TrendingUp, color: "text-secondary" },
    { label: "News Articles", value: news.length, icon: Newspaper, color: "text-info" },
    { label: "Job Listings", value: jobs.length, icon: Briefcase, color: "text-success" },
    { label: "Affiliate Links", value: affiliates.length, icon: LinkIcon, color: "text-accent" },
  ];

  useEffect(() => {
    const loadCurrentUser = async () => {
      const current = await authService.getCurrentUser();
      if (current?.user?.email) {
        setEmail(current.user.email);
      }
      if (current?.user?.id?.startsWith("env-backup-")) {
        setIsEnvBackupAdmin(true);
      }
    };

    loadCurrentUser();
  }, []);

  const handleAccountUpdate = async (e: FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    if (isEnvBackupAdmin) {
      setStatusMessage({
        type: "error",
        text: "Backup super admin account is managed through server .env and cannot be updated from dashboard.",
      });
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setStatusMessage({ type: "error", text: "Email is required." });
      return;
    }

    if (newPassword && newPassword.length < 6) {
      setStatusMessage({ type: "error", text: "Password must be at least 6 characters." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatusMessage({ type: "error", text: "New password and confirm password do not match." });
      return;
    }

    setLoadingAccount(true);
    try {
      const payload: { email?: string; password?: string } = { email: normalizedEmail };
      if (newPassword) {
        payload.password = newPassword;
      }

      const session = await authService.updateAccount(payload);
      setEmail(session.user.email);
      setNewPassword("");
      setConfirmPassword("");
      setStatusMessage({ type: "success", text: "Admin account updated successfully." });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to update admin account.",
      });
    } finally {
      setLoadingAccount(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-foreground mb-6">Admin Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="finance-card p-5">
            <s.icon size={22} className={s.color + " mb-2"} />
            <p className="text-sm text-muted-foreground">{s.label}</p>
            <p className="text-3xl font-display font-bold text-foreground">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="finance-card p-5 mt-6 max-w-2xl">
        <h2 className="text-lg font-display font-semibold text-foreground mb-4">Admin Account Settings</h2>
        <form onSubmit={handleAccountUpdate} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Admin Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
                disabled={isEnvBackupAdmin}
                required
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">New Password (optional)</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg border border-input bg-background text-sm"
                placeholder="Leave empty to keep current password"
                disabled={isEnvBackupAdmin}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Confirm New Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg border border-input bg-background text-sm"
                placeholder="Re-enter new password"
                disabled={isEnvBackupAdmin}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {statusMessage && (
            <p className={`text-sm ${statusMessage.type === "success" ? "text-success" : "text-destructive"}`}>
              {statusMessage.text}
            </p>
          )}

          <button
            type="submit"
            disabled={loadingAccount || isEnvBackupAdmin}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
          >
            {isEnvBackupAdmin ? "Managed via .env" : loadingAccount ? "Saving..." : "Update Admin Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
